import React, { useEffect, useMemo } from 'react';
import useProcessDiagramHelper from 'domain/gpo/cockpit/facade/diagram/useProcessDiagramHelper';
import { BpmnDiagramService } from 'domain/gpo/cockpit/facade/useBpmnJs';
import { useShowContext } from 'framework/show';
import { ProcessInstance } from 'domain/gpo/cockpit/instance/processInstanceService';
import type { SxProps, Theme } from '@mui/material';

const processDiagramSx: SxProps<Theme> = theme => ({
  border: '1px solid',
  borderColor: 'border.contrast',

  '@media (max-height: 989px)': {
    height: '400px',
  },
  '@media (min-height: 990px)': {
    height: '430px',
  },
  '@media (min-height: 900px)': {
    height: '645px',
  },

  '& .passed-activity:not(.djs-connection) .djs-visual > :nth-child(1)': {
    fill: `${theme.palette.success.bpmn} !important`,
  },

  '& .token, & .activeToken, & .incidentToken': {
    display: 'inline-block',
    minWidth: '10px',
    padding: '3px 7px',
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: 1,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    borderRadius: '10px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'text.primary',
  },
  '& .activeToken': {
    backgroundColor: 'info.light',
    color: 'text.primary',
  },
  '& .incidentToken': {
    backgroundColor: 'error.main',
    color: 'common.white',
  },
  '& .highlightActivity': {
    display: 'inline-block',
    backgroundColor: `${theme.palette.background.info} !important`,
    borderColor: `${theme.palette.info.dark} !important`,
    borderStyle: 'solid',
    borderWidth: '2px !important',
    borderRadius: '15px',
  },
  '& .due-date': {
    whiteSpace: 'pre',
  },
});

interface ProcessDiagramProps {
  instanceId: string;
  bpmnJs: BpmnDiagramService;
}

const ProcessDiagram = ({ bpmnJs }: ProcessDiagramProps) => {
  const { Diagram, loadXml } = bpmnJs;
  const { highlightPassedActivities, addTokens, addDueDates, addEventListeners } = useProcessDiagramHelper(bpmnJs);

  const { data: instance } = useShowContext<ProcessInstance>();

  useEffect(() => {
    if (instance.getValueBySource('xml')) {
      loadXml(instance.getValueBySource('xml')).then(() => {
        highlightPassedActivities(instance.getData());
        addTokens(instance.getData());
        addDueDates(instance.getData());
        addEventListeners();
      });
    }
  }, [instance.getValueBySource('xml')]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => <Diagram sx={processDiagramSx} />, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default ProcessDiagram;
